import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DeliverTxResponse } from '@cosmjs/stargate'
import { assert } from '@/utils'
import { queryKeys } from '@/query-keys'
import { assertIsDeliverTxSuccess, IBCTransferStatus, traceIbcStatus2, broadcastIbcTransaction2 } from '@/wallet-utils'
import { MutationParameters } from './types'
import { useSelectedChain } from '@/contexts/wallet'
import { flushSync } from 'react-dom'
import { useSelectedDenom } from '../../../../contexts/denom'

export interface BroadcastIbcTransferMutationReturnType {
  tx: DeliverTxResponse
  status: IBCTransferStatus
}

const useBroadcastIbcTransferMutation = ({ ibcTransferRaw, setIbcTransferTransaction }: MutationParameters) => {
  const { getSigningStargateClient, getAccount, getOfflineSigner, wallet } = useSelectedChain()

  const client = useQueryClient()

  const [denom] = useSelectedDenom()

  const handleMutation = async (): Promise<BroadcastIbcTransferMutationReturnType> => {
    assert(ibcTransferRaw, 'Unable to broadcast transaction without a signed transaction raw.')

    assert(wallet, `You are unable to send token without your ${denom} wallet instance.`)

    const [client, account] = await Promise.all([getSigningStargateClient(), getAccount()])
    const ibcParams = { client, denom, account, offlineSigner: getOfflineSigner(), wallet: wallet.name }
    const tx = await broadcastIbcTransaction2(ibcParams, ibcTransferRaw)

    assertIsDeliverTxSuccess(tx)

    const status = await traceIbcStatus2({ denom, tx })

    if (status === 'return-later') {
      return {
        tx: tx,
        status: 'return-later'
      }
    }

    // We'll handle this after balance is updated because gas fee is still consumed
    // @TODO: It's possible that we may have to wait for funds to be fully refunded
    // @TODO: Handle error - we probably need a flow for this
    if (status === 'timeout') {
      return {
        tx: tx,
        status: 'timeout'
      }
    }

    return {
      tx: tx,
      status: 'complete'
    }
  }

  const handleSuccess = async ({ tx }: BroadcastIbcTransferMutationReturnType) => {
    // This is the only way to allow React v18 to update the state first before
    // the next mutation (which requires this state to have a value) is executed.
    flushSync(() => {
      setIbcTransferTransaction(tx)
    })

    await client.invalidateQueries({ queryKey: queryKeys.chainBalancesBase })

    // We only want to invalidate the transaction history ibc balances after the account balances have been updated,
    // given that implementation for LSM IBC relies on stride's account balances. For the same reason, unlike most
    // IBC transactions, we won't be invalidating the ibc status query.
    await client.invalidateQueries({ queryKey: queryKeys.transactionHistoryLsmIbcBalancesBase })
  }

  return useMutation({
    mutationFn: handleMutation,
    onSuccess: handleSuccess
  })
}

export { useBroadcastIbcTransferMutation }
